import { Controller } from "@hotwired/stimulus";

// Live-calculates the tutor request monthly fee (subtotal) from
// hourly_rate × hours_per_lesson × lessons_per_week × 4.
// Below the duration-aware minimum (RM350 long-term, RM500 short-term)
// the fee box flips to the red error state and the wizard blocks Next.
// The server-derived `enforce` value (pending / confirmed only) keeps
// the red state and the gate scoped to the same records the server-side
// validation enforces.
export default class extends Controller {
  static targets = ["container", "breakdown", "amount", "label", "tip", "error", "minimumAmount", "errorMessage"];
  static values = {
    minimum: {type: Number, default: 350},
    shortTermMinimum: {type: Number, default: 500},
    enforce: {type: Boolean, default: false},
  };
  // Each name maps to a data-subtotal-*-class attribute on the Step 2
  // container. One Tailwind token per entry: classList.toggle rejects
  // strings that contain whitespace, so pass/fail pairs and their dark
  // variants are declared separately.
  static classes = [
    "containerPassBorder", "containerPassBorderDark",
    "containerFailBorder", "containerFailBorderDark",
    "containerPassBg", "containerPassBgDark",
    "containerFailBg", "containerFailBgDark",
    "labelPass", "labelPassDark", "labelFail", "labelFailDark",
    "amountPass", "amountPassDark", "amountFail", "amountFailDark",
  ];

  connect() {
    // The duration radios live in Step 1, outside this controller's element.
    // A document-level change listener is the minimal cross-step bridge.
    this.durationListener = (e) => {
      if (e.target.name === "tutor_request[duration_commitment]") this.update();
    };
    document.addEventListener("change", this.durationListener);
    this.update();
  }

  disconnect() {
    document.removeEventListener("change", this.durationListener);
  }

  // A checked "1 month or less" radio raises the floor to the short-term minimum (RM500).
  effectiveMinimum() {
    const checked = document.querySelector('input[name="tutor_request[duration_commitment]"]:checked');
    return checked && checked.value === "short_term"
      ? this.shortTermMinimumValue
      : this.minimumValue;
  }

  update() {
    const min = this.effectiveMinimum();

    // Update the three fee hints unconditionally, before the early return
    // for incomplete fee fields, so the hints never show a stale RM350.
    this.minimumAmountTargets.forEach(el => el.textContent = `RM${this.fmtMoney(min)}`);

    const rate = this.readField("hourly_rate");
    const hours = this.readField("hours_per_lesson");
    const lessons = this.readField("lessons_per_week");

    if (rate === null || hours === null || lessons === null) {
      this.containerTarget.classList.add("hidden");
      return;
    }

    const subtotal = rate * hours * lessons * 4;
    const below = this.enforceValue && subtotal < min;

    this.containerTarget.classList.remove("hidden");
    this.breakdownTarget.textContent =
      `RM${rate}/hr × ${this.fmtNumber(hours)} hr × ${lessons} ${lessons === 1 ? "lesson" : "lessons"}/week × 4 weeks`;
    this.amountTarget.textContent = `RM${this.fmtMoney(subtotal)}`;
    this.applyState(below, min);
  }

  // Swap the pass (blue) / fail (red) class sets, toggle the tip and
  // error lines, publish `data-fee-below` for the wizard's Next gate, and
  // refresh the error copy with the effective minimum (RM350 / RM500).
  applyState(below, min) {
    const container = this.containerTarget;

    container.classList.toggle(this.containerPassBorderClass, !below);
    container.classList.toggle(this.containerPassBorderDarkClass, !below);
    container.classList.toggle(this.containerFailBorderClass, below);
    container.classList.toggle(this.containerFailBorderDarkClass, below);
    container.classList.toggle(this.containerPassBgClass, !below);
    container.classList.toggle(this.containerPassBgDarkClass, !below);
    container.classList.toggle(this.containerFailBgClass, below);
    container.classList.toggle(this.containerFailBgDarkClass, below);

    this.labelTarget.classList.toggle(this.labelPassClass, !below);
    this.labelTarget.classList.toggle(this.labelPassDarkClass, !below);
    this.labelTarget.classList.toggle(this.labelFailClass, below);
    this.labelTarget.classList.toggle(this.labelFailDarkClass, below);

    this.amountTarget.classList.toggle(this.amountPassClass, !below);
    this.amountTarget.classList.toggle(this.amountPassDarkClass, !below);
    this.amountTarget.classList.toggle(this.amountFailClass, below);
    this.amountTarget.classList.toggle(this.amountFailDarkClass, below);

    this.tipTarget.classList.toggle("hidden", below);
    this.errorTarget.classList.toggle("hidden", !below);
    this.errorMessageTarget.textContent = this.errorCopy(min);
    container.dataset.feeBelow = String(below);
  }

  // One template for both branches: the number flips between RM350 and
  // RM500 with the duration selection.
  errorCopy(min) {
    return `Monthly fee must be at least RM${this.fmtMoney(min)}. Increase the hourly rate, hours per lesson, or lessons per week.`;
  }

  // Look up the selected value of a tutor_request field. Tries radio
  // buttons first, then falls back to a <select>. Returns a Number or
  // null when nothing is selected.
  readField(name) {
    const radio = this.element.querySelector(`input[name="tutor_request[${name}]"]:checked`);
    if (radio) {
      const n = parseFloat(radio.value);
      return Number.isFinite(n) ? n : null;
    }

    const select = this.element.querySelector(`select[name="tutor_request[${name}]"]`);
    if (select && select.value !== "") {
      const n = parseFloat(select.value);
      return Number.isFinite(n) ? n : null;
    }

    return null;
  }

  fmtNumber(n) {
    return Number.isInteger(n) ? n.toString() : n.toFixed(1).replace(/\.0$/, "");
  }

  fmtMoney(n) {
    return Math.round(n).toLocaleString("en-US");
  }
}
